/**
 * Supersede heuristic for pending agent tool-calls.
 *
 * DEC-5 / INV-6: a pending tool-call is "Superseded" only when a LATER pending
 * call has the SAME tool name AND the SAME recipient identity. Keying on the
 * tool name alone (the old rule) collapsed a legitimate multi-recipient
 * fan-out — e.g. N telegram sends to N distinct chats — into "all but the last
 * superseded".
 *
 * The recipient identity is NOT computed here: this host-layer helper must not
 * carry plugin-specific tool knowledge (which tool names carry a `chat_id`, etc.).
 * The caller supplies `recipientKeyOf`, which routes through the existing module
 * contribution seam (`runtime.agent.resolveAllowlistTarget` → each plugin's
 * `extractAllowlistTarget`). That keeps a single source of truth for "the
 * recipient identity of a tool-call". A tool with no recognised recipient → empty
 * key → the rule degrades to name-only matching (the previous behaviour).
 */
import type { PendingToolCall } from "@magnis/client-core";

/** True if `tc` is superseded: archived, or a later PENDING call shares its
 *  (name + recipient identity, per `recipientKeyOf`). */
export function isSuperseded(
  tc: PendingToolCall,
  toolCalls: readonly PendingToolCall[],
  archived: boolean,
  recipientKeyOf: (tc: PendingToolCall) => string,
): boolean {
  if (tc.status !== "pending") return false;
  if (archived) return true;
  // An approval-gated call is not a draft: it owns an `approval_id` and a
  // durable `pending_approval` row that only a decision can close, and the
  // backend's wait-set counts it regardless of what this screen shows. A
  // superseded card renders no buttons, so greying it out deletes the only
  // route to ending that wait — the episode stays parked in `needs_input`
  // while the feed claims there is nothing to decide. It would also
  // contradict `selectPendingPromptQueue`, which still offers it in the
  // pinned panel. @tested-by: tst_fe_agent_021, tst_fe_agentcore_008e
  if (tc.approvalId !== undefined) return false;
  const index = toolCalls.indexOf(tc);
  const key = recipientKeyOf(tc);
  return toolCalls.some(
    (other, i) =>
      i > index &&
      other.status === "pending" &&
      other.name === tc.name &&
      recipientKeyOf(other) === key,
  );
}
